import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { GridPaginationModel, GridRowParams } from '@mui/x-data-grid-pro';
import { toJS } from 'mobx';
import { observer } from 'mobx-react-lite';
import { IEmployeeSFilter } from 'src/@types/SearchFilterTypes';
import CDataGridPro from 'src/components/CDataGridPro';
import { CGridFooter } from 'src/components/custom-grid-data-components';
import { useDialogContext } from 'src/components/dialog/DialogContext';
import useCallApiWithLoading from 'src/hooks/useCallApiWithLoading';
import useGetGridCol, { IEmployeeGridRow } from 'src/hooks/useGetGridCol';
import { ContentBox, ContentBoxButtons, ContentButtonCreate } from 'src/layouts/form/FormButton';
import { useStores } from 'src/models';
import { INIT_PAGINATION, IPagination } from 'src/models/extensions/withPagination';
import { employeeApi } from 'src/service';
import { FORM_IDS, FormType } from 'src/static/constants/common/form';

import EmployeeFormDialog from './EmployeeFormDialog';

interface IEmployeeGridProps {
  searchData: IEmployeeSFilter;
}

export const EmployeeGrid = ({ searchData }: IEmployeeGridProps) => {
  const { alertStore } = useStores();
  const { callApiWithLoading } = useCallApiWithLoading();
  const { openDialog, closeDialog } = useDialogContext();
  const { getEmployeeGridCol } = useGetGridCol();
  const { t } = useTranslation();
  // 그리드 row 데이터
  const [gridRows, setGridRows] = useState<IEmployeeGridRow[]>([]);
  // 페이지네이션
  const [pagination, setPagination] = useState<IPagination>(INIT_PAGINATION);

  // 목록 조회
  const getEmployeeData = useCallback(async () => {
    const pageParams = {
      ...searchData,
      page: pagination.page,
      size: pagination.size,
    };
    await employeeApi.getEmployees(pageParams).then((res) => {
      if (res) {
        const newGridRows = res.data.map((employee, i) => {
          return { ...employee, id: employee.oprtrSid ?? i };
        });
        setGridRows(newGridRows);
        if (res.pagination) {
          setPagination(res.pagination);
        }
      }
    });
  }, [searchData, pagination.page, pagination.size]);

  useEffect(() => {
    getEmployeeData();
  }, [getEmployeeData, pagination.page, pagination.size]);

  // 등록 form dialog: 등록 버튼 선택 시
  const handleAdd = () => {
    openDialog({
      id: FORM_IDS.EMPLOYEE,
      formType: FormType.CREATE,
      dialogContent: (
        <EmployeeFormDialog formType={FormType.CREATE} onSubmitSuccess={handleSubmitSuccess} />
      ),
    });
  };

  // 그리드 더블 클릭 시: 수정 dialog open
  const handleRowDoubleClick = (param: GridRowParams<IEmployeeGridRow>) => {
    if (param.row.wthdYn) {
      alertStore.setProps({
        open: true,
        title: '삭제 계정',
        content: '삭제된 계정입니다.',
        category: 'error',
        hasCancelButton: true,
      });
      return;
    }
    callApiWithLoading(async () => {
      await employeeApi.getDetail(param.row.oprtrSid).then((res) => {
        if (res) {
          openDialog({
            id: FORM_IDS.EMPLOYEE,
            formType: FormType.UPDATE,
            dialogContent: (
              <EmployeeFormDialog
                formType={FormType.UPDATE}
                selectedEmployee={res.data}
                onSubmitSuccess={handleSubmitSuccess}
              />
            ),
          });
        }
      });
    });
  };

  const handleSubmitSuccess = () => {
    closeDialog(FORM_IDS.EMPLOYEE);
    getEmployeeData();
  };

  // 페이지 모델 변경 시
  const handlePaginationModelChange = (paginationModel: GridPaginationModel) => {
    const newPagination = {
      ...pagination,
      page: paginationModel.page + 1,
      size: paginationModel.pageSize,
    };
    setPagination(newPagination);
  };
  return (
    <ContentBox>
      <ContentBoxButtons>
        <ContentButtonCreate onClick={handleAdd}>{t('common.button.register')}</ContentButtonCreate>
      </ContentBoxButtons>

      <CDataGridPro
        columns={getEmployeeGridCol()}
        rows={gridRows}
        rowCount={toJS(pagination.totalElements)}
        paginationModel={{
          page: pagination.page - 1,
          pageSize: pagination.size,
        }}
        onPaginationModelChange={handlePaginationModelChange}
        slots={{ footer: CGridFooter }}
        onRowDoubleClick={handleRowDoubleClick}
        isRowSelectable={(params) => !params.row.wthdYn}
      />
    </ContentBox>
  );
};

export default observer(EmployeeGrid);
